import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useOrgKey } from '@reflex/ui/client/context';
import { ApiError } from '@reflex/ui/client/http';
import type { FlowTriggerExternalRefs } from '@reflex/plugin-api';
import { flowsApi } from './api.js';
import { useFlowActionCatalog, useFlowEventCatalog } from './useFlows.js';
import { findCatalogEntry } from '../shared/event-catalog.js';
import {
  EXTERNAL_REFS_MAX_IDS,
  collectExternalRefIds,
  externalRefFieldsFromIO,
  externalRefFieldsFromSchema,
  type ExternalRefField,
  type ExternalRefProfile,
} from '../shared/external-refs.js';

type OrgKey = ReturnType<typeof useOrgKey>;
const externalRefsKey = (org: OrgKey, fieldType: string, ids: string[]) =>
  ['flow-external-refs', org, fieldType, ids.join(',')] as const;

/**
 * {@link FlowTriggerExternalRefs} plus the field mapping that produced it,
 * so hosting surfaces can also decorate their own generic payload dumps
 * (renderers only receive the `FlowTriggerExternalRefs` slice). Fields
 * whose fieldType has no registered resolver (route 404) are dropped, so
 * consumers render those values as plain text instead of unresolved chips.
 */
export interface SchemaExternalRefs extends FlowTriggerExternalRefs {
  fields: ExternalRefField[];
}

/**
 * Resolve the external-reference ids a payload carries into display
 * profiles, given the payload's reference fields (from a JSON Schema's
 * `fieldType` annotations or an action's declared outputs). Resolution
 * goes through `GET /flows/external-refs`, one query per fieldType,
 * cached per org.
 *
 * Returns undefined when there are no reference fields with values, so
 * callers can pass the result straight through as the optional renderer
 * prop. Failed or partial lookups keep `profiles` sparse — consumers
 * degrade to raw ids.
 */
export function useExternalRefsForFields(
  refFields: ExternalRefField[],
  payload: Record<string, unknown> | null | undefined,
): SchemaExternalRefs | undefined {
  const orgKey = useOrgKey();

  const { fields, lookups } = useMemo(() => {
    const byFieldType = collectExternalRefIds(refFields, payload ?? null);
    return {
      fields: refFields,
      lookups: [...byFieldType].map(([fieldType, ids]) => ({
        fieldType,
        ids: ids.slice(0, EXTERNAL_REFS_MAX_IDS),
      })),
    };
  }, [refFields, payload]);

  const combined = useQueries({
    queries: lookups.map(({ fieldType, ids }) => ({
      queryKey: externalRefsKey(orgKey, fieldType, ids),
      queryFn: () => flowsApi.getExternalRefs(fieldType, ids),
      staleTime: 5 * 60 * 1000,
      // A failed lookup just means raw ids stay visible; don't hammer the
      // provider API from every mounted bubble — and never retry a 404
      // (no resolver registered for the fieldType).
      retry: (failureCount: number, error: unknown) =>
        !(error instanceof ApiError && error.status === 404) && failureCount < 1,
    })),
    combine: (results) => {
      // Null prototype: profile keys are ids echoed from webhook payloads,
      // so `__proto__`/`constructor` must land as plain own properties.
      const profiles: Record<string, ExternalRefProfile> = Object.create(null);
      let pending = false;
      const unknown: string[] = [];
      results.forEach((result, index) => {
        if (result.isPending) pending = true;
        if (result.data?.ok) {
          for (const [id, profile] of Object.entries(result.data.refs)) {
            profiles[id] = profile;
          }
        }
        if (result.error instanceof ApiError && result.error.status === 404) {
          unknown.push(lookups[index].fieldType);
        }
      });
      // Joined string keeps the combined result structurally stable for
      // react-query's memoization (a Set would change identity per render).
      return { profiles, pending, unknownFieldTypes: unknown.sort().join(',') };
    },
  });

  return useMemo(() => {
    if (lookups.length === 0) return undefined;
    const unknown = new Set(
      combined.unknownFieldTypes ? combined.unknownFieldTypes.split(',') : [],
    );
    const resolvableFields = fields.filter((field) => !unknown.has(field.fieldType));
    if (resolvableFields.length === 0) return undefined;
    return { profiles: combined.profiles, pending: combined.pending, fields: resolvableFields };
  }, [lookups.length, combined, fields]);
}

const NO_FIELDS: ExternalRefField[] = [];

/** {@link useExternalRefsForFields} against a JSON Schema's annotations. */
export function useExternalRefsForSchema(
  schema: Record<string, unknown> | null | undefined,
  payload: Record<string, unknown> | null | undefined,
): SchemaExternalRefs | undefined {
  const fields = useMemo(() => externalRefFieldsFromSchema(schema), [schema]);
  return useExternalRefsForFields(fields, payload);
}

/**
 * {@link useExternalRefsForFields} against an event trigger's catalog
 * payload schema.
 */
export function useTriggerExternalRefs(
  eventRef: { source: string; event: string } | undefined,
  triggerData: Record<string, unknown> | null | undefined,
): SchemaExternalRefs | undefined {
  const { data: catalog } = useFlowEventCatalog();
  const source = eventRef?.source;
  const event = eventRef?.event;
  const schema = useMemo(() => {
    const entry =
      source && event && catalog ? findCatalogEntry(catalog, { source, event }) : undefined;
    return entry?.payloadJsonSchema ?? null;
  }, [source, event, catalog]);
  return useExternalRefsForSchema(schema, triggerData);
}

/**
 * Reference resolution for an action node's params AND result: params
 * fields come from the catalog's `paramsJsonSchema` annotations, result
 * fields from the declared outputs' `fieldType`s. This is what lets a run
 * view badge the concrete ids an action ran with and returned (e.g.
 * `slack.reply_to_thread`'s channel, `slack.fetch_message`'s sender).
 */
export function useActionExternalRefs(
  actionRef: { provider: string; operation: string } | undefined,
  params: Record<string, unknown> | null | undefined,
  result: Record<string, unknown> | null | undefined,
): { params: SchemaExternalRefs | undefined; result: SchemaExternalRefs | undefined } {
  const { data: catalog } = useFlowActionCatalog();
  const provider = actionRef?.provider;
  const operation = actionRef?.operation;
  const entry = useMemo(
    () =>
      provider && operation && catalog
        ? catalog.find((e) => e.provider === provider && e.operation === operation)
        : undefined,
    [provider, operation, catalog],
  );
  const paramFields = useMemo(
    () => externalRefFieldsFromSchema(entry?.paramsJsonSchema),
    [entry?.paramsJsonSchema],
  );
  const resultFields = useMemo(
    () => (entry?.outputs ? externalRefFieldsFromIO(entry.outputs) : NO_FIELDS),
    [entry?.outputs],
  );
  return {
    params: useExternalRefsForFields(paramFields, params),
    result: useExternalRefsForFields(resultFields, result),
  };
}
